// src/pages/AnalyseStatusPage.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import axios from 'axios';
import { videoHeaders, isVerificatieFout, meldVerificatieVerlopen } from '../utils/videoVerificatie';
import { nietAangekomenTekst } from '../utils/videoUpload';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
const POLL_INTERVAL_MS = 5000; // Poll every 5 seconds

function getStatusDisplay(analyse) {
  if (analyse.niet_aangekomen) {
    return {
      label: 'Niet aangekomen',
      description: nietAangekomenTekst(analyse),
      className: 'status-error',
    };
  }
  switch (analyse.status) {
    case 'uploading':
      return {
        label: 'Video uploaden',
        description: 'Je video wordt geüpload. Daarna begint de analyse vanzelf.',
        className: 'status-uploading',
      };
    case 'processing':
      return {
        label: 'Analyse bezig',
        description: 'De AI analyseert je video aan de hand van het doorloopje. Dit kan 2-10 minuten duren.',
        className: 'status-processing',
      };
    case 'done':
      return {
        label: 'Analyse voltooid',
        description: 'De analyse is klaar. Bekijk je rapport.',
        className: 'status-done',
      };
    case 'error':
      return {
        label: 'Fout opgetreden',
        description: '',
        className: 'status-error',
      };
    default:
      return { label: 'Onbekend', description: '', className: '' };
  }
}

function AnalyseStatusPage() {
  const { documentId } = useParams();
  const { currentUser } = useDoorloopjes();
  const [analyse, setAnalyse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [justCompleted, setJustCompleted] = useState(false);
  const pollRef = useRef(null);
  const prevStatusRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const jwt = localStorage.getItem('jwt');
      const res = await axios.get(
        `${STRAPI_URL}/api/video-analyses/${documentId}?populate[doorloopje][fields][0]=documentId&populate[doorloopje][fields][1]=title`,
        { headers: videoHeaders(jwt) }
      );
      const data = res.data.data;
      // Detect transition to done for success animation
      if (data.status === 'done' && prevStatusRef.current && prevStatusRef.current !== 'done') {
        setJustCompleted(true);
      }
      prevStatusRef.current = data.status;
      setAnalyse(data);
      setLoading(false);

      // Stop polling if terminal state
      if (data.status === 'done' || data.status === 'error') {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch (err) {
      if (isVerificatieFout(err)) return meldVerificatieVerlopen();
      console.error('Status fetch error:', err);
      setError(
        err.response?.status === 403
          ? 'Je hebt geen toegang tot deze analyse.'
          : err.response?.status === 404
          ? 'Analyse niet gevonden.'
          : 'De stand van je analyse laden lukte niet. Probeer het opnieuw.'
      );
      setLoading(false);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }, [documentId]);

  // Polling lifecycle — also handles STAT-02 (resumes on re-mount)
  useEffect(() => {
    // Fetch immediately on mount
    fetchStatus();

    // Start polling interval
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);

    // Cleanup on unmount
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [fetchStatus]);

  // Resume polling on tab re-focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        analyse &&
        !['done', 'error'].includes(analyse.status)
      ) {
        fetchStatus(); // Immediate fetch on tab return
        // Restart interval if not already running
        if (!pollRef.current) {
          pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchStatus, analyse]);

  // Access gate
  if (!currentUser?.has_video_analysis) {
    return <Navigate to="/dashboard" replace />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="page-container video-analyse-page">
        <div className="page-introduction">
          <h1>Video Analyse</h1>
        </div>
        <main className="video-analyse-content">
          <p>Laden...</p>
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page-container video-analyse-page">
        <div className="page-introduction">
          <h1>Video Analyse</h1>
        </div>
        <main className="video-analyse-content">
          <p className="field-error">{error}</p>
          <Link to="/video-analyse" className="back-link">
            Terug naar overzicht
          </Link>
        </main>
      </div>
    );
  }

  const statusInfo = getStatusDisplay(analyse);

  return (
    <div className="page-container video-analyse-page">
      <div className="page-introduction">
        <nav className="breadcrumbs">
          <Link to="/video-analyse">Video Analyse</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Status</span>
        </nav>
        <h1>Analyse status</h1>
      </div>
      <main className="video-analyse-content">
        {justCompleted && (
          <div className="success-banner">
            <span className="success-check">&#10003;</span>
            De analyse is klaar. Je rapport staat klaar.
          </div>
        )}
        <div className={`status-card${justCompleted ? ' status-card--success' : ''}`}>
          <div className={`status-indicator ${statusInfo.className}`}>
            <span className="status-dot"></span>
            <span className="status-label">{statusInfo.label}</span>
          </div>

          {analyse.doorloopje && (
            <p className="status-detail">
              <strong>Doorloopje:</strong> {analyse.doorloopje.title}
            </p>
          )}

          {analyse.original_filename && (
            <p className="status-detail">
              <strong>Bestand:</strong> {analyse.original_filename}
            </p>
          )}

          <p className="status-description">{statusInfo.description}</p>

          {analyse.status === 'error' && analyse.error_message && (
            <p className="field-error">{analyse.error_message}</p>
          )}

          {analyse.status === 'done' && (
            <Link
              to={`/video-analyse/${documentId}/rapport`}
              className="btn-primary-action"
              style={{ marginTop: '1rem' }}
            >
              Rapport bekijken
            </Link>
          )}

          {analyse.status === 'error' && (
            <Link
              to="/video-analyse/nieuw"
              className="btn-primary-action"
              style={{ marginTop: '1rem' }}
            >
              Opnieuw proberen
            </Link>
          )}

          {analyse.niet_aangekomen && (
            <Link
              to={`/video-analyse/nieuw?hervat=${analyse.documentId}`}
              className="btn-primary-action"
              style={{ marginTop: '1rem' }}
            >
              Opnieuw proberen
            </Link>
          )}

          {((analyse.status === 'uploading' && !analyse.niet_aangekomen) || analyse.status === 'processing') && (
            <div className="polling-indicator">
              <span className="polling-dot"></span>
              <span>Status wordt automatisch bijgewerkt...</span>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AnalyseStatusPage;
